import { NavLink, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, Users, Zap, FolderKanban, CreditCard, Settings as SettingsIcon, LogOut, ExternalLink } from "lucide-react";
import Logo from "../../components/Logo";
import { useAdminUser } from "../../components/ProtectedAdmin";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Leads CRM", icon: Users },
  { to: "/admin/bookings", label: "Rush Hour Ops", icon: Zap },
  { to: "/admin/payments", label: "Payments", icon: CreditCard },
  { to: "/admin/case-studies", label: "Case Studies", icon: FolderKanban },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminLayout() {
  const { user, logout } = useAdminUser();

  return (
    <div className="min-h-screen bg-navy flex" data-testid="admin-layout">
      <aside className="w-60 shrink-0 border-r border-white/10 bg-navy-900 hidden lg:flex flex-col">
        <div className="p-6 border-b border-white/10"><Logo compact /></div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-semibold transition-colors ${
                  isActive ? "bg-flame/15 text-flame" : "text-slate-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <Link to="/" data-testid="admin-view-site" className="flex items-center gap-2 text-xs text-slate-500 hover:text-flame transition-colors px-4 py-2">
            <ExternalLink className="w-3.5 h-3.5" /> View site
          </Link>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="glass-header sticky top-0 z-30 px-6 py-4 flex items-center justify-between">
          <div className="flex lg:hidden items-center gap-4 overflow-x-auto">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `text-xs font-semibold whitespace-nowrap ${isActive ? "text-flame" : "text-slate-400"}`}>
                {n.label}
              </NavLink>
            ))}
          </div>
          <div className="hidden lg:block">
            <p className="font-heading font-bold text-white text-sm">Built2Last.live Admin</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-xs font-semibold text-white" data-testid="admin-user-name">{user?.name}</p>
              <p className="text-[10px] font-mono tracking-widest text-flame uppercase">{user?.role?.replace("_", " ")}</p>
            </div>
            {user?.picture && <img src={user.picture} alt="" className="w-9 h-9 rounded-full border border-white/20" referrerPolicy="no-referrer" />}
            <button onClick={logout} data-testid="admin-logout-btn" className="text-slate-400 hover:text-flame transition-colors" aria-label="Log out">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
